import Guid from "_shared/guids/guid";
import { retiredListLoader } from "_shared/_views/list-pages/retired-list-redirect";
import { LoaderFunctionArgs, redirect } from "react-router";
import {
  ARCHIVED_STAGE,
  RETIRED_WORK_AGREEMENT_TABS,
  STAGE_PARAMETER,
  WORK_AGREEMENTS_PATH,
} from "work/work-agreements/_views/work-agreement-list-chips";

type Options = {
  // Where to go if URL shape is invalid
  baseRedirect: string;
  // Require :id?
  requireId?: boolean;
  // Validate :action
  allowedActions?: readonly string[];
};

export function makeWorkAgreementLoader(opts: Options) {
  const {
    baseRedirect,
    requireId = false,
    allowedActions,
  } = opts;

  return async function loader({ params }: LoaderFunctionArgs) {
    const id = params["id"];
    const action = params["action"];

    if (allowedActions && action && !allowedActions.includes(action)) {
      return redirect(baseRedirect);
    }

    if (requireId) {
      if (!id) {
        console.warn("Missing work agreement id in URL");
        return redirect(baseRedirect);
      }
      if (!Guid.isValid(id)) {
        console.warn(`Invalid work agreement id format: ${id}`);
        return redirect(baseRedirect);
      }
    }

    return null;
  };
}

export function withDefaultTab(
  defaultTab: string,
  inner?: (a: LoaderFunctionArgs) => Promise<Response | null> | Response | null
) {
  return async (args: LoaderFunctionArgs) => {
    const { request, params } = args;

    if (!params.pageTab) {
      const url = new URL(request.url);
      const basePath = url.pathname.replace(/\/+$/, ""); // trim trailing slash
      return redirect(`${basePath}/${defaultTab}${url.search}`);
    }

    return inner ? inner(args) : null;
  };
}

/**
 * `/work/:pageTab`. The list has no tabs since #6716, so `/work/active` opens the list and
 * `/work/archived` its Archived chip, each keeping the link's other parameters; any other segment
 * opens the list.
 */
export const workAgreementListLoader = retiredListLoader({
  basePath: WORK_AGREEMENTS_PATH,
  retired: RETIRED_WORK_AGREEMENT_TABS,
  inner: () => redirect(WORK_AGREEMENTS_PATH),
});

export const activeWorkAgreementLoader = makeWorkAgreementLoader({
  baseRedirect: WORK_AGREEMENTS_PATH,
  allowedActions: ["view"],
});

export const archivedWorkAgreementLoader = makeWorkAgreementLoader({
  baseRedirect: `${WORK_AGREEMENTS_PATH}?${STAGE_PARAMETER}=${ARCHIVED_STAGE}`,
  allowedActions: ["view"],
});
